import dayjs from '~/timeInit';

import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import type { ChartDataPoint, CsAgentRosterEntry, CsDashboardFeatureStats, MergedAgentRow } from './types';

const EXCLUDED_AGENT_EMAILS = new Set(['external+prod-sierra@luxgroup.com']);

export function isExcludedAgentEmail(email: string | null | undefined): boolean {
  return EXCLUDED_AGENT_EMAILS.has((email ?? '').toLowerCase());
}

// `yyyymmdd` is the format svc-reporting returns dates in (e.g. `20260514`);
// The dashboard only ever uses these as grouping keys or for display formatting — never for date
// arithmetic — so we keep them as strings end-to-end.
export function formatJsonDate(yyyymmdd: string): string {
  if (yyyymmdd.length !== 8) return yyyymmdd;
  return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}`;
}

export function getCountColor(count: number, maxCount: number): string {
  if (maxCount === 0) return 'rgb(255, 200, 200)';

  const ratio = count / maxCount;
  const red = Math.round(255 - ratio * 55);
  const green = Math.round(200 + ratio * 55);
  const blue = 200;
  return `rgb(${red}, ${green}, ${blue})`;
}

export function getCurrentSydneyDateKey(now: Date = new Date()): string {
  return dayjs(now).tz(SYDNEY_TIMEZONE).format('YYYYMMDD');
}

export function onlyCompletedSydneyDates(dates: string[], currentSydneyDateKey = getCurrentSydneyDateKey()): string[] {
  return dates.filter((date) => date < currentSydneyDateKey);
}

export function onlyCompletedSydneyChartData<T extends ChartDataPoint>(
  chartData: T[],
  currentSydneyDateKey = getCurrentSydneyDateKey(),
): T[] {
  return chartData.filter((point) => String(point.date) < currentSydneyDateKey);
}

/**
 * Build chart points from the server-supplied per-feature `dailyTotals`.
 * No SUM in JS — svc-reporting already aggregated across agents.
 * Not retiring this function as it will be used again when filtering logic
 * is moved server-side. buildChartDataFromAgentRows was a temp patch for showcase.
 **/
export function buildChartDataFromServerTotals(
  features: CsDashboardFeatureStats[] | undefined,
  featureIds: string[],
): ChartDataPoint[] {
  if (!features) return [];

  const dateSet = new Set<string>();
  features.forEach((feature) => {
    feature.dailyTotals.forEach(({ date }) => dateSet.add(date));
  });

  const dates = Array.from(dateSet).sort();
  const totalsByFeature = new Map(
    features.map((feature) => [
      feature.featureId,
      new Map(feature.dailyTotals.map((dailyTotal) => [dailyTotal.date, dailyTotal.count])),
    ]),
  );

  return dates.map((date) => {
    const point: ChartDataPoint = { date };
    featureIds.forEach((featureId) => {
      point[featureId] = totalsByFeature.get(featureId)?.get(date) ?? 0;
    });
    return point;
  });
}

export function buildChartDataFromAgentRows(featureIds: string[], agentRows: MergedAgentRow[]): ChartDataPoint[] {
  const dateSet = new Set<string>();
  agentRows.forEach((row) => {
    featureIds.forEach((featureId) => {
      Object.keys(row.countsByFeatureAndDate[featureId] ?? {}).forEach((date) => dateSet.add(date));
    });
  });

  const dates = Array.from(dateSet).sort();
  return dates.map((date) => {
    const point: ChartDataPoint = { date };
    featureIds.forEach((featureId) => {
      point[featureId] = agentRows.reduce(
        (total, row) => total + (row.countsByFeatureAndDate[featureId]?.[date] ?? 0),
        0,
      );
    });
    return point;
  });
}

/**
 * When a team leader filter is active, the server's all-agent totals don't
 * apply — we have to re-sum from the per-agent rows for just that leader's
 * agents. This is the only place the frontend does SUM aggregation. The roster
 * (agent -> team leader) now comes from svc-reporting; if the team-leader
 * filter is ever pushed down as a query param, this branch can be deleted.
 */
export function buildChartDataForFilteredTeam(
  featureIds: string[],
  agentRows: MergedAgentRow[],
  filteredAgentEmails: Set<string>,
): ChartDataPoint[] {
  const dateSet = new Set<string>();
  agentRows.forEach((row) => {
    featureIds.forEach((featureId) => {
      Object.keys(row.countsByFeatureAndDate[featureId] ?? {}).forEach((date) => dateSet.add(date));
    });
  });

  const dates = Array.from(dateSet).sort();
  return dates.map((date) => {
    const point: ChartDataPoint = { date };
    featureIds.forEach((featureId) => {
      let total = 0;
      agentRows.forEach((row) => {
        if (!filteredAgentEmails.has(row.agentEmail)) return;
        total += row.countsByFeatureAndDate[featureId]?.[date] ?? 0;
      });
      point[featureId] = total;
    });
    return point;
  });
}

export function buildCsAgentRosterRows(agents: CsAgentRosterEntry[]): MergedAgentRow[] {
  const map = new Map<string, MergedAgentRow>();

  agents.forEach((agent) => {
    const agentEmail = (agent.email ?? '').trim().toLowerCase();
    if (!agentEmail || isExcludedAgentEmail(agentEmail)) return;

    if (!map.has(agentEmail)) {
      map.set(agentEmail, {
        agentEmail,
        teamLeader: agent.teamLeader ?? null,
        department: agent.department ?? null,
        subDepartment: agent.subDepartment ?? null,
        countsByFeatureAndDate: {},
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => a.agentEmail.localeCompare(b.agentEmail));
}

export function buildMergedAgentRows(
  features: CsDashboardFeatureStats[] | undefined,
  rosterRows?: MergedAgentRow[],
): MergedAgentRow[] {
  const map = new Map<string, MergedAgentRow>();
  const hasRoster = rosterRows !== undefined;

  rosterRows?.forEach((row) => {
    map.set(row.agentEmail, { ...row, countsByFeatureAndDate: {} });
  });

  features?.forEach(({ featureId, agents }) => {
    agents.forEach(({ agentEmail, dailyCounts }) => {
      // Stats are keyed by email. A stats row with no email can't be identified
      // or matched to the roster, so it's skipped.
      const email = (agentEmail ?? '').trim().toLowerCase();
      if (!email || isExcludedAgentEmail(email)) return;

      if (!map.has(email)) {
        if (hasRoster) return;
        map.set(email, {
          agentEmail: email,
          teamLeader: null,
          department: null,
          subDepartment: null,
          countsByFeatureAndDate: {},
        });
      }
      const row = map.get(email)!;
      row.countsByFeatureAndDate[featureId] = {};
      dailyCounts.forEach(({ date, count }) => {
        row.countsByFeatureAndDate[featureId][date] = count;
      });
    });
  });

  return Array.from(map.values()).sort((a, b) => a.agentEmail.localeCompare(b.agentEmail));
}

export function getAllDates(rows: MergedAgentRow[], featureIds: string[]): string[] {
  const dateSet = new Set<string>();
  rows.forEach((row) => {
    featureIds.forEach((featureId) => {
      Object.keys(row.countsByFeatureAndDate[featureId] ?? {}).forEach((date) => dateSet.add(date));
    });
  });
  return Array.from(dateSet).sort();
}

export function computeMaxCellCount(rows: MergedAgentRow[], featureIds: string[], dates: string[]): number {
  let max = 0;
  rows.forEach((row) => {
    featureIds.forEach((featureId) => {
      const byDate = row.countsByFeatureAndDate[featureId] ?? {};
      dates.forEach((date) => {
        const c = byDate[date] ?? 0;
        if (c > max) max = c;
      });
    });
  });
  return max;
}
